import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type LlmMetricsGroup } from '../../api/adminApi';

type TimeRange = 1 | 6 | 24 | 168;
const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '7d', value: 168 },
];

const pct = (r: number) => `${(r * 100).toFixed(1)}%`;
const usd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;

export function LlmMetrics() {
  const [hours, setHours] = useState<TimeRange>(24);

  const { data, isLoading, error } = useQuery({
    queryKey: ['llm-metrics', hours],
    queryFn: () => adminApi.getLlmMetrics(hours),
    refetchInterval: 60_000,
  });

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>LLM Metrics</h2>
        <p className="section-description">
          Per-call LLM latency, reliability, tokens, and cost — grouped by operation and model.
        </p>
      </div>

      {/* Time range selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TIME_RANGES.map(r => (
          <button
            key={r.value}
            className={`settings-tab ${hours === r.value ? 'active' : ''}`}
            onClick={() => setHours(r.value)}
            style={{ padding: '6px 16px', fontSize: 13 }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="loading">Loading metrics...</div>}
      {error && <div className="error">Error: {(error as Error).message}</div>}

      {data && (
        <>
          {/* Summary row */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
            <SummaryCard label="Total Calls" value={data.totalCalls.toLocaleString()} />
            <SummaryCard label="p95 Latency" value={`${data.overall.p95LatencyMs.toLocaleString()}ms`} />
            <SummaryCard label="p50 Latency" value={`${data.overall.p50LatencyMs.toLocaleString()}ms`} />
            <SummaryCard label="Error Rate" value={pct(data.overall.errorRate)} alert={data.overall.errorRate > 0} />
            <SummaryCard label="429 Rate" value={pct(data.overall.rateLimitRate)} alert={data.overall.rateLimitRate > 0} />
            <SummaryCard label="Failover Rate" value={pct(data.overall.failoverRate)} alert={data.overall.failoverRate > 0} />
            <SummaryCard label="Total Cost" value={usd(data.overall.totalCostUsd)} />
          </div>

          {/* Groups table */}
          {data.groups.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)' }}>No LLM calls found for this period.</p>
          ) : (
            <table className="admin-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Operation</th>
                  <th>Model</th>
                  <th style={{ textAlign: 'right' }}>Calls</th>
                  <th style={{ textAlign: 'right' }}>p50</th>
                  <th style={{ textAlign: 'right' }}>p95</th>
                  <th style={{ textAlign: 'right' }}>Err</th>
                  <th style={{ textAlign: 'right' }}>429</th>
                  <th style={{ textAlign: 'right' }}>Failover</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.groups.map(g => <GroupRow key={`${g.operation}::${g.model}`} g={g} />)}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div style={{
      background: 'var(--bg-tertiary)',
      borderRadius: 8,
      padding: '12px 20px',
      minWidth: 130,
      ...(alert ? { boxShadow: 'inset 0 0 0 1px var(--color-error)' } : {}),
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: alert ? 'var(--color-error)' : 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function GroupRow({ g }: { g: LlmMetricsGroup }) {
  const danger = (r: number) => (r > 0 ? { color: 'var(--color-error)', fontWeight: 600 } : { color: 'var(--text-tertiary)' });
  return (
    <tr>
      <td>{g.operation}</td>
      <td><code style={{ fontSize: 13 }}>{g.model}</code></td>
      <td style={{ textAlign: 'right' }}>{g.callCount}</td>
      <td style={{ textAlign: 'right' }}>{g.p50LatencyMs}ms</td>
      <td style={{ textAlign: 'right' }}>{g.p95LatencyMs}ms</td>
      <td style={{ textAlign: 'right', ...danger(g.errorRate) }}>{pct(g.errorRate)}</td>
      <td style={{ textAlign: 'right', ...danger(g.rateLimitRate) }}>{pct(g.rateLimitRate)}</td>
      <td style={{ textAlign: 'right', ...danger(g.failoverRate) }}>{pct(g.failoverRate)}</td>
      <td style={{ textAlign: 'right', fontWeight: 600 }}>{usd(g.totalCostUsd)}</td>
    </tr>
  );
}
